import {
  deriveToofiArtifactMetadata,
  isToofiArtifactBlock,
  TOOFI_ARTIFACT_MIGRATION_KEY,
  type ToofiArtifactPayload,
  type ToofiArtifactRecord,
  type ToofiArtifactRecordIndexItem,
  type ToofiArtifactScope,
  type ToofiArtifactSurfaceOrigin,
} from "@/lib/toofi-artifact-types";
import {
  readToofiArtifactIndex,
  removeToofiArtifactIndexItem,
  upsertToofiArtifactIndexItem,
} from "@/lib/toofi-artifact-index";
import type { ReaderAiBlock } from "@/types/agentic-chat";

const DB_NAME = "athr-toofi-artifacts";
const DB_VERSION = 1;
const STORE_NAME = "artifact-payloads";
const LEGACY_MCQ_ARTIFACT_KEY = "athr.reader.mcq-artifacts";
const LEGACY_NOTE_ARTIFACT_KEY = "athr.reader.note-artifacts";

type ToofiArtifactPayloadRecord = {
  id: string;
  payload: ToofiArtifactPayload;
};

type LegacyMcqArtifact = {
  id: string;
  question?: string;
  options?: string[];
  correctIndex?: number | null;
  pageNumber?: number;
  createdAt?: string;
};

type LegacyNoteArtifact = {
  id: string;
  title?: string;
  content?: string;
  pageNumber?: number;
  createdAt?: string;
};

export type SaveToofiArtifactInput = Omit<ToofiArtifactRecord, "createdAt" | "updatedAt" | "saved" | "pinned"> & {
  createdAt?: number;
  updatedAt?: number;
  pinned?: boolean;
};

const now = () => Date.now();

const readJson = <T>(key: string): T | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const getIndexedDb = () => {
  if (typeof window === "undefined") return null;
  return window.indexedDB ?? null;
};

// In-memory fallback for artifacts
const artifactMemoryStore = new Map<string, ToofiArtifactPayload>();

const openArtifactDb = async (): Promise<IDBDatabase | null> =>
  await new Promise((resolve) => {
    try {
      const indexedDb = getIndexedDb();
      if (!indexedDb) {
        resolve(null);
        return;
      }
      const request = indexedDb.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME, { keyPath: "id" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });

const runStoreRequest = async <T>(
  mode: IDBTransactionMode,
  handler: (store: any, resolve: (value: T) => void, reject: (reason?: unknown) => void) => void,
): Promise<T> => {
  const database = await openArtifactDb();
  if (!database) {
    // Memory Fallback
    return new Promise<T>((resolve, reject) => {
      const store = {
        get: (id: string) => ({ result: { id, payload: artifactMemoryStore.get(id) }, onsuccess: null } as any),
        put: (item: ToofiArtifactPayloadRecord) => {
          artifactMemoryStore.set(item.id, item.payload);
          return { onsuccess: null } as any;
        },
        delete: (id: string) => {
          artifactMemoryStore.delete(id);
          return { onsuccess: null } as any;
        },
      };
      handler(store as any, resolve, reject);
      // Simulate IDB async behavior
      setTimeout(() => {
        (store.get as any)().onsuccess?.();
        (store.put as any)().onsuccess?.();
        (store.delete as any)().onsuccess?.();
      }, 0);
    });
  }
  return await new Promise<T>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    handler(store, resolve, reject);
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => database.close();
  });
};

const putArtifactPayload = async (record: ToofiArtifactRecord) => {
  await runStoreRequest<void>("readwrite", (store, resolve, reject) => {
    const request = store.put({
      id: record.id,
      payload: record.payload,
    } satisfies ToofiArtifactPayloadRecord);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

const getArtifactPayload = async (artifactId: string): Promise<ToofiArtifactPayload | null> =>
  await runStoreRequest<ToofiArtifactPayload | null>("readonly", (store, resolve, reject) => {
    const request = store.get(artifactId);
    request.onsuccess = () => resolve((request.result as ToofiArtifactPayloadRecord | undefined)?.payload ?? null);
    request.onerror = () => reject(request.error);
  });

const deleteArtifactPayload = async (artifactId: string) => {
  await runStoreRequest<void>("readwrite", (store, resolve, reject) => {
    const request = store.delete(artifactId);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

const toIndexItem = (record: ToofiArtifactRecord): ToofiArtifactRecordIndexItem => ({
  id: record.id,
  kind: record.kind,
  scope: record.scope,
  threadId: record.threadId,
  messageId: record.messageId,
  blockId: record.blockId,
  resourceId: record.resourceId,
  linkedPlanId: record.linkedPlanId,
  surfaceOrigin: record.surfaceOrigin,
  title: record.title,
  preview: record.preview,
  createdAt: record.createdAt,
  updatedAt: record.updatedAt,
  saved: record.saved,
  pinned: record.pinned,
  sourcePage: record.sourcePage,
  sourcePageLabel: record.sourcePageLabel,
  sourcePageRoute: record.sourcePageRoute,
  sourcePageKind: record.sourcePageKind,
  sourcePagePath: record.sourcePagePath,
});

const buildArtifactId = (scope: ToofiArtifactScope, threadId: string, messageId: string, blockId: string) =>
  `artifact:${scope}:${threadId}:${messageId}:${blockId}`;

export const buildToofiArtifactRecord = ({
  block,
  blockId,
  threadId,
  messageId,
  scope,
  resourceId = null,
  linkedPlanId = null,
  surfaceOrigin = "unknown",
  sourcePage = null,
  sourcePageLabel = null,
  sourcePageRoute = null,
  sourcePageKind = null,
  sourcePagePath = null,
}: {
  block: ReaderAiBlock;
  blockId: string;
  threadId: string;
  messageId: string;
  scope: ToofiArtifactScope;
  resourceId?: string | null;
  linkedPlanId?: string | null;
  surfaceOrigin?: ToofiArtifactSurfaceOrigin;
  sourcePage?: number | null;
  sourcePageLabel?: string | null;
  sourcePageRoute?: string | null;
  sourcePageKind?: "reader" | "workspace" | null;
  sourcePagePath?: string | null;
}): SaveToofiArtifactInput | null => {
  if (!isToofiArtifactBlock(block)) return null;
  const metadata = deriveToofiArtifactMetadata(block);
  return {
    id: buildArtifactId(scope, threadId, messageId, blockId),
    kind: metadata.kind,
    scope,
    threadId,
    messageId,
    blockId,
    resourceId,
    linkedPlanId,
    surfaceOrigin,
    title: metadata.title,
    preview: metadata.preview,
    sourcePage,
    sourcePageLabel,
    sourcePageRoute,
    sourcePageKind,
    sourcePagePath,
    payload: block,
  };
};

export const saveToofiArtifact = async (input: SaveToofiArtifactInput): Promise<ToofiArtifactRecord> => {
  const timestamp = now();
  const record: ToofiArtifactRecord = {
    ...input,
    createdAt: input.createdAt ?? timestamp,
    updatedAt: input.updatedAt ?? timestamp,
    saved: true,
    pinned: Boolean(input.pinned),
  };
  await putArtifactPayload(record);
  upsertToofiArtifactIndexItem(toIndexItem(record));
  return record;
};

export const getToofiArtifact = async (artifactId: string): Promise<ToofiArtifactRecord | null> => {
  const indexItem = readToofiArtifactIndex().find((item) => item.id === artifactId);
  if (!indexItem) return null;
  const payload = await getArtifactPayload(artifactId);
  if (!payload) return null;
  return {
    ...indexItem,
    payload,
  };
};

export const listToofiArtifacts = async (): Promise<ToofiArtifactRecordIndexItem[]> => readToofiArtifactIndex();

export const deleteToofiArtifact = async (artifactId: string) => {
  await deleteArtifactPayload(artifactId);
  removeToofiArtifactIndexItem(artifactId);
};

export const setToofiArtifactPinned = async (artifactId: string, pinned: boolean) => {
  const item = readToofiArtifactIndex().find((entry) => entry.id === artifactId);
  if (!item) return;
  upsertToofiArtifactIndexItem({
    ...item,
    pinned,
    updatedAt: now(),
  });
};

const mapLegacyMcqArtifacts = (items: LegacyMcqArtifact[]): SaveToofiArtifactInput[] =>
  items
    .filter((item) => typeof item?.id === "string")
    .map((item) => {
      const payload: ToofiArtifactPayload = {
        type: "mcq",
        id: item.id,
        prompt: item.question ?? "Quick quiz",
        options: (item.options ?? []).map((option, index) => ({
          id: `legacy-mcq-${item.id}-${index + 1}`,
          label: option,
        })),
        correctIndex: typeof item.correctIndex === "number" ? item.correctIndex : null,
      };
      const createdAt = item.createdAt ? new Date(item.createdAt).getTime() : now();
      return {
        id: `legacy-reader-mcq:${item.id}`,
        kind: "mcq",
        scope: "reader",
        threadId: "legacy-reader",
        messageId: `legacy-reader:${item.id}`,
        blockId: item.id,
        resourceId: null,
        linkedPlanId: null,
        surfaceOrigin: "reader",
        title: "Quick quiz",
        preview: item.question?.trim() || "Quick quiz",
        sourcePage: item.pageNumber ?? null,
        sourcePageLabel: item.pageNumber ? `Page ${item.pageNumber}` : null,
        sourcePageRoute: null,
        sourcePageKind: "reader",
        sourcePagePath: null,
        payload,
        createdAt,
        updatedAt: createdAt,
      };
    });

const mapLegacyNoteArtifacts = (items: LegacyNoteArtifact[]): SaveToofiArtifactInput[] =>
  items
    .filter((item) => typeof item?.id === "string")
    .map((item) => {
      const payload: ToofiArtifactPayload = {
        type: "note",
        id: item.id,
        title: item.title ?? "Study note",
        content: item.content ?? "",
        saved: true,
      };
      const createdAt = item.createdAt ? new Date(item.createdAt).getTime() : now();
      return {
        id: `legacy-reader-note:${item.id}`,
        kind: "note",
        scope: "reader",
        threadId: "legacy-reader",
        messageId: `legacy-reader:${item.id}`,
        blockId: item.id,
        resourceId: null,
        linkedPlanId: null,
        surfaceOrigin: "reader",
        title: item.title?.trim() || "Study note",
        preview: item.content?.trim() || "Study note",
        sourcePage: item.pageNumber ?? null,
        sourcePageLabel: item.pageNumber ? `Page ${item.pageNumber}` : null,
        sourcePageRoute: null,
        sourcePageKind: "reader",
        sourcePagePath: null,
        payload,
        createdAt,
        updatedAt: createdAt,
      };
    });

let migrationPromise: Promise<void> | null = null;

export const ensureToofiArtifactMigration = async () => {
  if (typeof window === "undefined") return;
  if (window.localStorage.getItem(TOOFI_ARTIFACT_MIGRATION_KEY) === "done") return;
  if (migrationPromise) {
    await migrationPromise;
    return;
  }

  migrationPromise = (async () => {
    const existingIds = new Set(readToofiArtifactIndex().map((item) => item.id));
    const legacyMcqs = readJson<LegacyMcqArtifact[]>(LEGACY_MCQ_ARTIFACT_KEY) ?? [];
    const legacyNotes = readJson<LegacyNoteArtifact[]>(LEGACY_NOTE_ARTIFACT_KEY) ?? [];
    const candidates = [...mapLegacyMcqArtifacts(legacyMcqs), ...mapLegacyNoteArtifacts(legacyNotes)];

    for (const artifact of candidates) {
      if (existingIds.has(artifact.id)) continue;
      await saveToofiArtifact(artifact);
    }

    window.localStorage.setItem(TOOFI_ARTIFACT_MIGRATION_KEY, "done");
  })().finally(() => {
    migrationPromise = null;
  });

  await migrationPromise;
};

